<template>
  <div class="price" :class="[
    { 'animate__animated animate__zoomOutLeft': isDeleting },
    { 'price--deleting': wasDeleted },
  ]">
    <div class="column column--branch">
      <span>{{ priceCompany.company.name }}</span>
    </div>
    <div class="column column--agent">
      <span @click="canEdit && (showPopupEditer = true)">
        <transition name="fade">
          <PopupEditer v-if="showPopupEditer" :selected="priceCompany.agent_provision * 100"
            @togglePopup="showPopupEditer = $event" @submit="changeAgentProvision($event)" />
        </transition>
        {{ (agentProvision * 100).toFixed(2) }}%
      </span>
    </div>
    <div class="column column--company">
      <span>{{ (priceCompany.company_provision * 100).toFixed(2) }}%</span>
    </div>

    <div v-if="canDelete" class="icon icon__delete" @click="deletePrice()"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import PopupEditer from "@/partials/PopupEditer";
import eventBus from "@/eventBus";

defineOptions({
  name: "PriceCompanyRow"
});

const props = defineProps({
  priceCompany: {
    required: true,
    type: Object,
  },
  salesCategoryId: {
    required: true,
    type: Number
  },
  selectSale: {
    required: true,
    type: Function,
  },
  teamId: {
    required: true,
    type: Number,
  },
  secondary: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

// State
const isDeleting = ref(false);
const wasDeleted = ref(false);
const showPopupEditer = ref(false);
const agentProvision = ref("");
const agentProvisionCopy = ref("");
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const canEdit = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("sales_category_price_edit", props.teamId);
});
const canDelete = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("sales_category_price_delete", props.teamId);
});
const refreshCategories = () => store.dispatch(
  props.secondary ? "getSecondSaleCategoriesByTeam" : "getSaleCategoriesByTeam",
  props.teamId
);

// Methods
const reloadData = async () => {
  await refreshCategories();
  props.selectSale(props.salesCategoryId);
};

const deletePrice = async () => {
  if (!canDelete.value) return;
  isDeleting.value = true;
  await store.dispatch("deleteSaleCategoryPriceCompany", { id: props.priceCompany.id, teamId: props.teamId });
  wasDeleted.value = true;

  setTimeout(async () => {
    await refreshCategories();
    // Note: ensure price object exists or fallback to salesCategoryId
    props.selectSale(props.priceCompany.sales_category_id || props.salesCategoryId);
  }, 300);
};

const changeAgentProvision = async (provision) => {
  if (!canEdit.value) return;
  if (
    provision <= 100 &&
    provision > 0 &&
    provision !== agentProvisionCopy.value
  ) {
    agentProvision.value = provision / 100;
    await store.dispatch("updateSaleCategoryPriceCompany", {
      saleCategoryPriceCompanyId: props.priceCompany.id,
      provision,
      teamId: props.teamId,
    });
    reloadData();
  }
};

// Lifecycle
onMounted(() => {
  agentProvision.value = props.priceCompany.agent_provision;
  agentProvisionCopy.value = props.priceCompany.agent_provision;

  eventBus.on("priceCompanyAdded", reloadData);
});

onUnmounted(() => {
  eventBus.off("priceCompanyAdded", reloadData);
});
</script>

<style lang="scss" scoped>
.price {
  border-bottom: 1px solid var(--border-15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  position: relative;
  max-height: 47px;
  transition: all 0.3s ease;
  background: #F8FAFB;

  &--deleting {
    padding: 0px 20px;
    max-height: 0px;
    overflow: hidden;
    border: 0;
  }

  .icon {
    position: absolute;
    right: 25px;
  }
}
</style>
